/* ============================================================================
 * Shopify storefront annotation overlay (admin-gated)
 * ---------------------------------------------------------------------------
 * - GATED: only logged-in Supabase admins see anything. Press Ctrl/Cmd+Shift+A
 *   to open a login form; the notes data is protected by RLS (authenticated
 *   only), so shoppers can't see or query notes even via DevTools.
 * - Stays logged in across reloads/closes via the Supabase refresh-token
 *   session (until you Sign out).
 * - "Add note" -> click any element to drop a pin anchored to it. Pins track
 *   the element through scroll/resize/layout shifts.
 * - Click a pin to open its note; click again (or X) to close.
 * - Per-admin pins: initial + a stable colour from the admin's name.
 * - Per note: completion tick (green ring when done), priority 1-5, and a
 *   reply thread. Filter pins by priority (show/hide each, or "solo").
 *
 * Adding admins: call public.create_admin('email','password','Name') in
 * Supabase (SQL). Any authenticated user in the project can use this tool.
 * ========================================================================== */
(function () {
  'use strict';

  // ---- Config ---------------------------------------------------------------
  var SUPABASE_URL = 'https://pbaapftqudwcgiojennw.supabase.co';
  var SUPABASE_KEY = 'sb_publishable_Z9GYBgx5UVgtKABzpeLA5w_9hUYlaIk';
  var PAGE_PATH = location.pathname;
  var STORAGE_SESSION = 'sma_session';

  if (window.__smaLoaded) return;
  window.__smaLoaded = true;

  // ---- State ----------------------------------------------------------------
  var session = null;     // {access_token, refresh_token, expires_at, name, email}
  var me = null;          // display name
  var notes = [];
  var overlayOn = false;
  var addMode = false;
  var activeCard = null;
  var visiblePri = { 1: true, 2: true, 3: true };
  var visibleAuthors = {};   // name -> bool (absent = visible)
  var authorColors = {};     // name -> unique palette colour
  var filterPanel = null;
  var dimLevel = parseFloat(localStorage.getItem('sma_dim_level'));
  if (isNaN(dimLevel)) dimLevel = 0.30;            // on by default, just under the old 0.35
  dimLevel = Math.max(0, Math.min(0.45, dimLevel)); // slider range 0–45%
  var dimEl = null;
  var blockerEl = null;
  var composer = null;   // the open "new note" composer card, if any
  var hoverEl = null;    // element currently targeted in add mode (arrow-adjustable)
  var dirtyCheck = null; // () => bool, set while an editor (composer/edit) is open
  var userMenu = null;
  var booted = false;
  var barOn = false;     // whether the toolbar (Notes button) is shown
  var stylesInjected = false;

  // author colours — greens/oranges/teals/browns only, so they never clash
  // with the priority gradient (red -> purple -> blue)
  var PALETTE = ['#3cb44b', '#f58231', '#9A6324', '#808000', '#e07a00',
    '#1f7a4d', '#00897b', '#6d4c41', '#827717', '#2e7d32'];

  function hashIndex(name) {
    name = name || '?';
    var h = 0;
    for (var i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return h % PALETTE.length;
  }
  function colorFor(name) { return PALETTE[hashIndex(name)]; }
  // give each distinct author a UNIQUE palette colour (probe past collisions)
  function recomputeAuthorColors() {
    var set = {}; notes.forEach(function (n) { set[n.author_name] = 1; });
    if (me) set[me] = 1;
    var list = Object.keys(set).sort(), used = {}, map = {};
    list.forEach(function (name) {
      var idx = hashIndex(name), i = 0;
      while (used[(idx + i) % PALETTE.length] && i < PALETTE.length) i++;
      var ci = (idx + i) % PALETTE.length;
      used[ci] = true; map[name] = PALETTE[ci];
    });
    authorColors = map;
  }
  function displayColor(name) { return authorColors[name] || colorFor(name); }
  function letterFor(name) { var t = (name || '').trim(); return (t[0] || '?').toUpperCase(); }
  // priority 1 = red (most important), 2 = purple, 3 = blue (least)
  function priorityColor(p) {
    p = Math.max(1, Math.min(3, p || 2));
    var t = (p - 1) / 2;                 // 0 at p1 (red), 0.5 = purple, 1 at p3 (blue)
    var hi = [225, 29, 72], lo = [67, 99, 216];
    var c = hi.map(function (v, i) { return Math.round(v + t * (lo[i] - v)); });
    return 'rgb(' + c[0] + ',' + c[1] + ',' + c[2] + ')';
  }
  function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 8); }

  // ---- Auth -----------------------------------------------------------------
  function loadSession() {
    try { return JSON.parse(localStorage.getItem(STORAGE_SESSION)); } catch (e) { return null; }
  }
  function setSession(j) {
    session = {
      access_token: j.access_token,
      refresh_token: j.refresh_token,
      expires_at: Date.now() + ((j.expires_in || 3600) * 1000),
      name: (j.user && j.user.user_metadata && j.user.user_metadata.name) ||
            (j.user && j.user.email) || (session && session.name) || 'Admin',
      email: (j.user && j.user.email) || (session && session.email) || ''
    };
    me = session.name;
    localStorage.setItem(STORAGE_SESSION, JSON.stringify(session));
  }
  function clearSession() { session = null; me = null; localStorage.removeItem(STORAGE_SESSION); }

  function authPost(path, body) {
    return fetch(SUPABASE_URL + '/auth/v1/' + path, {
      method: 'POST',
      headers: { apikey: SUPABASE_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); });
  }
  function login(email, password) {
    return authPost('token?grant_type=password', { email: email, password: password })
      .then(function (res) {
        if (!res.ok || !res.j.access_token) {
          throw new Error((res.j && (res.j.error_description || res.j.msg || res.j.error)) || 'Login failed');
        }
        setSession(res.j); return true;
      });
  }
  function refresh() {
    if (!session || !session.refresh_token) return Promise.resolve(false);
    return authPost('token?grant_type=refresh_token', { refresh_token: session.refresh_token })
      .then(function (res) {
        if (!res.ok || !res.j.access_token) { clearSession(); return false; }
        setSession(res.j); return true;
      }).catch(function () { return false; });
  }
  function ensureToken() {
    if (!session) return Promise.resolve(null);
    if (Date.now() < session.expires_at - 60000) return Promise.resolve(session.access_token);
    return refresh().then(function (ok) { return ok ? session.access_token : null; });
  }

  // ---- Supabase REST (authenticated) ----------------------------------------
  function api(path, opts) {
    opts = opts || {};
    return ensureToken().then(function (token) {
      if (!token) throw new Error('Not signed in');
      return doFetch(token);
    });
    function doFetch(token) {
      var headers = { apikey: SUPABASE_KEY, Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' };
      if (opts.headers) for (var k in opts.headers) headers[k] = opts.headers[k];
      return fetch(SUPABASE_URL + '/rest/v1/' + path, {
        method: opts.method || 'GET', headers: headers,
        body: opts.body ? JSON.stringify(opts.body) : undefined
      }).then(function (res) {
        if (res.status === 401) {
          return refresh().then(function (ok) {
            if (ok) return doFetch(session.access_token);
            requireLogin();
            throw new Error('Session expired');
          });
        }
        return res.text().then(function (txt) {
          if (!res.ok) throw new Error(txt || res.status);
          return txt ? JSON.parse(txt) : null;
        });
      });
    }
  }

  function loadNotes() {
    var q = 'annotations?page_path=eq.' + encodeURIComponent(PAGE_PATH) +
      '&select=*&order=created_at.asc';
    return api(q).then(function (rows) {
      notes = (rows || []).map(function (r) {
        r._res = null; r._pin = null;
        if (!Array.isArray(r.replies)) r.replies = [];
        return r;
      });
      recomputeAuthorColors();
      renderPins();
    }).catch(function (e) { console.error('[annotations] load failed', e); });
  }
  function createNote(row) {
    return api('annotations', { method: 'POST', headers: { Prefer: 'return=representation' }, body: row })
      .then(function (rows) { return rows[0]; });
  }
  function updateNote(id, patch) {
    return api('annotations?id=eq.' + id, { method: 'PATCH', headers: { Prefer: 'return=representation' }, body: patch })
      .then(function (rows) { return rows[0]; });
  }
  function deleteNote(id) { return api('annotations?id=eq.' + id, { method: 'DELETE' }); }

  // ---- Anchoring ------------------------------------------------------------
  // an id is only safe to anchor on if exactly one element has it (themes
  // sometimes assign duplicate or garbage ids like "[object HTMLSelectElement]")
  function uniqueId(el) {
    if (!el || !el.id) return false;
    try { return document.querySelectorAll('#' + CSS.escape(el.id)).length === 1; } catch (e) { return false; }
  }
  function cssPath(el) {
    if (!(el instanceof Element)) return '';
    var parts = [];
    while (el && el.nodeType === 1 && el !== document.body && parts.length < 12) {
      var sel = el.nodeName.toLowerCase();
      if (uniqueId(el)) { parts.unshift('#' + CSS.escape(el.id)); break; }
      var nth = 1, sib = el;
      while ((sib = sib.previousElementSibling)) { if (sib.nodeName.toLowerCase() === sel) nth++; }
      sel += ':nth-of-type(' + nth + ')';
      parts.unshift(sel);
      el = el.parentElement;
    }
    return parts.join(' > ');
  }
  function elAttrs(el) {
    var o = {};
    if (el && el.getAttribute) {
      var src = el.getAttribute('src'); if (src) o.src = src.split('?')[0].split('/').pop();
      var alt = el.getAttribute('alt'); if (alt) o.alt = alt;
      var aria = el.getAttribute('aria-label'); if (aria) o.aria = aria;
      var title = el.getAttribute('title'); if (title) o.title = title;
    }
    return o;
  }
  // nearest stable ancestor to fall back to (prefers one with an id)
  function pickHost(el) {
    var cur = el, best = null, steps = 0;
    while (cur && cur !== document.body && steps < 8) {
      if (uniqueId(cur)) return cur;
      var r = cur.getBoundingClientRect();
      if (!best && r.width >= 120 && r.height >= 40) best = cur;
      cur = cur.parentElement; steps++;
    }
    return best || el.parentElement || el;
  }
  function buildAnchor(el, relX, relY) {
    var a = {
      sel: cssPath(el), tag: el.nodeName.toLowerCase(),
      text: (el.textContent || '').trim().slice(0, 80), attrs: elAttrs(el),
      rx: relX, ry: relY
    };
    var host = pickHost(el);
    if (host && host !== el) {
      var hr = host.getBoundingClientRect(), er = el.getBoundingClientRect();
      var cx = er.left + relX * er.width, cy = er.top + relY * er.height;
      a.host = {
        sel: cssPath(host), tag: host.nodeName.toLowerCase(),
        rx: hr.width ? (cx - hr.left) / hr.width : 0.5,
        ry: hr.height ? (cy - hr.top) / hr.height : 0.5
      };
    }
    return a;
  }
  // resolve a {sel,tag,text,attrs} spec to a live element via several strategies
  function matchEl(spec) {
    if (spec.sel) {
      var lead = spec.sel.split('>')[0].trim();   // skip selectors hung off a non-unique id
      var ok = true;
      if (lead.charAt(0) === '#') { try { ok = document.querySelectorAll(lead).length === 1; } catch (e) { ok = false; } }
      if (ok) { try { var e = document.querySelector(spec.sel); if (e) return e; } catch (x) { } }
    }
    if (spec.tag) {
      var list = document.getElementsByTagName(spec.tag), at = spec.attrs || {}, i;
      for (i = 0; i < list.length; i++) {
        var g = list[i];
        if (at.src && (g.getAttribute('src') || '').split('?')[0].slice(-at.src.length) === at.src) return g;
        if (at.alt && g.getAttribute('alt') === at.alt) return g;
        if (at.aria && g.getAttribute('aria-label') === at.aria) return g;
        if (at.title && g.getAttribute('title') === at.title) return g;
      }
      if (spec.text) {
        for (i = 0; i < list.length; i++) {
          if (((list[i].textContent || '').trim().slice(0, 80)) === spec.text) return list[i];
        }
      }
    }
    return null;
  }
  function computeAnchor(n) {
    if (n.anchor) {                                   // rich anchor (new notes)
      var e = matchEl(n.anchor);
      if (e) return { el: e, rx: n.anchor.rx, ry: n.anchor.ry };
      if (n.anchor.host) { var h = matchEl(n.anchor.host); if (h) return { el: h, rx: n.anchor.host.rx, ry: n.anchor.host.ry }; }
      return null;
    }
    // legacy flat columns (older notes)
    if (n.anchor_selector) { try { var le = document.querySelector(n.anchor_selector); if (le) return { el: le, rx: n.rel_x, ry: n.rel_y }; } catch (x) { } }
    if (n.anchor_tag && n.anchor_text) {
      var l = document.getElementsByTagName(n.anchor_tag);
      for (var i = 0; i < l.length; i++) {
        if (((l[i].textContent || '').trim().slice(0, 80)) === n.anchor_text) return { el: l[i], rx: n.rel_x, ry: n.rel_y };
      }
    }
    if (n.anchor_selector) {                          // host fallback: leading #id of the stored selector
      var first = n.anchor_selector.split('>')[0].trim();
      if (first.charAt(0) === '#') { try { var hh = document.querySelector(first); if (hh) return { el: hh, rx: 0.5, ry: 0.2 }; } catch (x) { } }
    }
    return null;
  }
  // is the anchored element actually visible? (so notes hide with their element)
  function isVisible(el) {
    if (!el) return false;
    if (!el.getClientRects().length) return false;          // display:none (self or ancestor)
    var cs = getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.visibility === 'collapse') return false;
    if (parseFloat(cs.opacity || '1') === 0) return false;
    return true;
  }
  // cached resolve; re-resolves while unresolved so late-rendered DOM is caught
  function resolveAnchor(n) {
    if (n._res && n._res.el && document.contains(n._res.el)) return n._res;
    var res = computeAnchor(n);
    n._res = res || null;
    return res;
  }

  // ---- DOM scaffolding ------------------------------------------------------
  var root, fab, addBtn, filterBtn, hint, highlight;
  function isOurs(node) { return root && root.contains(node); }

  function ensureStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    var css = '' +
      '.sma-root{position:fixed;inset:0;z-index:2147483000;pointer-events:none;font:14px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#1a1a1a}' +
      '.sma-root *{box-sizing:border-box}' +
      '.sma-fab{position:fixed;left:18px;top:18px;pointer-events:auto;display:flex;flex-direction:column;align-items:flex-start;gap:8px}' +
      '.sma-btn{pointer-events:auto;border:0;border-radius:999px;padding:10px 14px;font-weight:600;cursor:pointer;box-shadow:0 2px 10px rgba(0,0,0,.2);background:#1a1a1a;color:#fff}' +
      '.sma-btn.sma-on{background:#2563eb}' +
      '.sma-btn.sma-ghost{background:#fff;color:#1a1a1a}' +
      '.sma-pin{position:fixed;width:33px;height:33px;margin:-16.5px 0 0 -16.5px;border-radius:50%;color:#fff;font-weight:700;font-size:15px;display:flex;align-items:center;justify-content:center;cursor:pointer;pointer-events:auto;box-shadow:0 3px 8px rgba(0,0,0,.45);border:2px solid #fff;user-select:none}' +
      '.sma-pin.sma-done{box-shadow:0 3px 8px rgba(0,0,0,.45)}' +
      '.sma-pin.sma-orphan{outline:2px dashed rgba(17,17,17,.55);outline-offset:2px}' +
      '.sma-pin-user{position:absolute;bottom:-11px;left:-11px;width:24px;height:24px;border-radius:50%;font-size:14px;font-weight:700;display:flex;align-items:center;justify-content:center;color:#fff;border:1.5px solid #fff;box-shadow:0 1px 2px rgba(0,0,0,.3)}' +
      '.sma-card{position:fixed;pointer-events:auto;max-width:450px;min-width:345px;max-height:62vh;overflow:auto;background:#fff;border-radius:10px;box-shadow:0 6px 24px rgba(0,0,0,.25);padding:12px;z-index:2147483600}' +
      '.sma-close{position:absolute;top:6px;right:8px;border:0;background:none;cursor:pointer;font-size:18px;line-height:1;color:#aaa}' +
      '.sma-meta{display:flex;align-items:center;gap:8px;margin-bottom:6px;padding-right:16px}' +
      '.sma-badge{width:22px;height:22px;border-radius:50%;color:#fff;font-weight:700;font-size:11px;display:flex;align-items:center;justify-content:center;flex:0 0 auto}' +
      '.sma-author{font-weight:600;font-size:13px}' +
      '.sma-body{white-space:pre-wrap;word-break:break-word;font-size:13px;color:#222}' +
      '.sma-ctl{display:flex;align-items:center;gap:14px;margin-top:10px;font-size:12px;color:#444}' +
      '.sma-ctl-top{margin:0 0 10px;padding:2px 20px 10px 0;border-bottom:1px solid #eee}' +
      '.sma-ctl label{display:flex;align-items:center;gap:5px;cursor:pointer}' +
      '.sma-ctl select{font:inherit;font-size:12px;padding:2px 4px;border:1px solid #ddd;border-radius:6px}' +
      '.sma-actions{display:flex;gap:8px;margin-top:10px;justify-content:flex-end}' +
      '.sma-actions.sma-split{justify-content:space-between}' +
      '.sma-link{background:none;border:0;cursor:pointer;font-size:12px;color:#2563eb;padding:2px 4px}' +
      '.sma-link.sma-danger{color:#dc2626}' +
      '.sma-card textarea{width:100%;min-height:180px;border:1px solid #ddd;border-radius:6px;padding:8px;font:inherit;resize:vertical}' +
      '.sma-thread{margin-top:8px;border-top:1px solid #eee;padding-top:6px}' +
      '.sma-reply{display:flex;gap:6px;margin-top:8px}' +
      '.sma-reply .sma-badge{width:18px;height:18px;font-size:10px}' +
      '.sma-reply-main{flex:1}' +
      '.sma-reply-author{font-weight:600;font-size:11px}' +
      '.sma-reply-body{font-size:12px;white-space:pre-wrap;word-break:break-word}' +
      '.sma-hint{position:fixed;left:50%;top:14px;transform:translateX(-50%);pointer-events:none;background:#2563eb;color:#fff;padding:8px 14px;border-radius:999px;box-shadow:0 2px 10px rgba(0,0,0,.2);font-weight:600}' +
      '.sma-hl{position:fixed;pointer-events:none;border:2px solid #2563eb;background:rgba(37,99,235,.12);border-radius:3px;z-index:2147483500;display:none}' +
      '.sma-filter{position:fixed;pointer-events:auto;background:#fff;border-radius:10px;box-shadow:0 6px 24px rgba(0,0,0,.25);padding:10px 12px;width:220px;max-height:80vh;overflow:auto;font-size:13px}' +
      '.sma-fhead{margin:12px 0 4px;font-size:11px;color:#888;font-weight:700;text-transform:uppercase;letter-spacing:.03em}' +
      '.sma-dot{width:12px;height:12px;border-radius:50%;flex:0 0 auto}' +
      '.sma-filter h4{margin:0 0 8px;font-size:12px;color:#666;font-weight:700;text-transform:uppercase;letter-spacing:.03em}' +
      '.sma-frow{display:flex;align-items:center;gap:8px;margin:5px 0}' +
      '.sma-frow label{display:flex;align-items:center;gap:6px;cursor:pointer;flex:1}' +
      '.sma-solo{background:#eef;border:0;border-radius:6px;color:#2563eb;font-size:11px;font-weight:600;cursor:pointer;padding:3px 8px}' +
      '.sma-solo.sma-on{background:#2563eb;color:#fff}' +
      '.sma-solo[disabled]{opacity:.35;cursor:default}' +
      '.sma-dim{position:fixed;inset:0;pointer-events:none}' +
      '.sma-frow input[type=range]{flex:1;min-width:0;accent-color:#2563eb}' +
      '.sma-dimval{width:34px;text-align:right;color:#666;font-size:12px;flex:0 0 auto}' +
      '.sma-noterow{display:flex;align-items:center;gap:8px;margin:3px 0;cursor:pointer;padding:3px 4px;border-radius:6px}' +
      '.sma-noterow:hover{background:#f1f5ff}' +
      '.sma-notetext{font-size:12px;color:#333;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0}' +
      '.sma-noterow.sma-off .sma-notetext{color:#aaa}' +
      '.sma-blocker{position:fixed;inset:0;background:transparent;pointer-events:auto}' +
      '.sma-fall{margin-top:8px;width:100%;background:#f4f4f5;border:0;border-radius:6px;padding:6px;cursor:pointer;font-weight:600;font-size:12px}' +
      '.sma-login{position:fixed;inset:0;pointer-events:auto;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;z-index:2147483600}' +
      '.sma-login-box{background:#fff;border-radius:12px;padding:22px;width:300px;box-shadow:0 10px 40px rgba(0,0,0,.3)}' +
      '.sma-login-box h3{margin:0 0 14px;font-size:16px}' +
      '.sma-login-box input{width:100%;padding:9px 10px;border:1px solid #ddd;border-radius:8px;margin-bottom:10px;font:inherit}' +
      '.sma-login-box .primary{width:100%;background:#2563eb;color:#fff;border:0;border-radius:8px;padding:10px;font-weight:600;cursor:pointer}' +
      '.sma-login-err{color:#dc2626;font-size:12px;margin-bottom:8px;min-height:14px}' +
      '.sma-login-cancel{display:block;width:100%;text-align:center;margin-top:8px;background:none;border:0;color:#888;cursor:pointer;font-size:12px}' +
      '.sma-menu{position:fixed;pointer-events:auto;background:#fff;border-radius:10px;box-shadow:0 6px 24px rgba(0,0,0,.25);padding:10px;min-width:170px;font-size:13px;z-index:2147483600}' +
      '.sma-menu-id{font-size:12px;color:#666;margin-bottom:8px;word-break:break-all}' +
      '.sma-menu-out{width:100%;background:#fdecec;border:0;border-radius:6px;padding:8px;cursor:pointer;font-weight:600;font-size:12px;color:#dc2626}';
    var style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
  }

  function ensureRoot() {
    if (root) return;
    ensureStyles();
    root = document.createElement('div');
    root.className = 'sma-root';
    highlight = document.createElement('div');
    highlight.className = 'sma-hl';
    root.appendChild(highlight);
    document.body.appendChild(root);
  }

  function buildUI() {
    ensureRoot();
    if (fab) return;
    fab = document.createElement('div');
    fab.className = 'sma-fab';

    var toggle = document.createElement('button');
    toggle.className = 'sma-btn'; toggle.textContent = 'Notes';
    toggle.onclick = function () { setOverlay(!overlayOn); };

    addBtn = document.createElement('button');
    addBtn.className = 'sma-btn sma-ghost'; addBtn.textContent = '+ Add note';
    addBtn.style.display = 'none';
    addBtn.onclick = function () { setAddMode(!addMode); };

    filterBtn = document.createElement('button');
    filterBtn.className = 'sma-btn sma-ghost'; filterBtn.textContent = 'View';
    filterBtn.style.display = 'none';
    filterBtn.onclick = toggleFilterPanel;

    var who = document.createElement('button');
    who.className = 'sma-btn sma-ghost'; who.title = 'Account';
    who.onclick = toggleUserMenu;

    fab._toggle = toggle; fab._who = who;
    fab.appendChild(toggle); fab.appendChild(addBtn);
    fab.appendChild(filterBtn); fab.appendChild(who);
    root.appendChild(fab);
    refreshFab();
  }

  function refreshFab() {
    if (!fab) return;
    fab._toggle.classList.toggle('sma-on', overlayOn);
    fab._who.textContent = me ? (letterFor(me) + ' ' + me) : '';
    fab._who.style.background = me ? colorFor(me) : '#fff';
    fab._who.style.color = me ? '#fff' : '#1a1a1a';
    addBtn.style.display = overlayOn ? '' : 'none';
    filterBtn.style.display = overlayOn ? '' : 'none';
    fab._who.style.display = overlayOn ? '' : 'none';   // name bubble only when opened
  }

  // ---- Login ----------------------------------------------------------------
  function showLogin() {
    ensureRoot();
    if (root.querySelector('.sma-login')) return;
    var ov = document.createElement('div'); ov.className = 'sma-login';
    var box = document.createElement('div'); box.className = 'sma-login-box';
    var h = document.createElement('h3'); h.textContent = 'Admin sign in';
    var email = document.createElement('input');
    email.type = 'email'; email.placeholder = 'Email'; email.autocomplete = 'username';
    var pw = document.createElement('input');
    pw.type = 'password'; pw.placeholder = 'Password'; pw.autocomplete = 'current-password';
    var err = document.createElement('div'); err.className = 'sma-login-err';
    var btn = document.createElement('button'); btn.className = 'primary'; btn.textContent = 'Sign in';
    var cancel = document.createElement('button'); cancel.className = 'sma-login-cancel'; cancel.textContent = 'Cancel';

    function submit() {
      err.textContent = ''; btn.disabled = true; btn.textContent = 'Signing in…';
      login(email.value.trim(), pw.value).then(function () {
        ov.remove(); start();
      }).catch(function (e) {
        err.textContent = e.message || 'Login failed';
        btn.disabled = false; btn.textContent = 'Sign in';
      });
    }
    btn.onclick = submit;
    pw.addEventListener('keydown', function (e) { if (e.key === 'Enter') submit(); });
    email.addEventListener('keydown', function (e) { if (e.key === 'Enter') pw.focus(); });
    cancel.onclick = function () {
      ov.remove();
      if (!fab) { root.remove(); root = null; }   // nothing else open: clean up
    };

    box.appendChild(h); box.appendChild(email); box.appendChild(pw);
    box.appendChild(err); box.appendChild(btn); box.appendChild(cancel);
    ov.appendChild(box); root.appendChild(ov);
    email.focus();
  }
  function requireLogin() {
    closeCard(); closeFilterPanel(); setOverlay(false);
    if (fab) { fab.remove(); fab = null; }
    showLogin();
  }

  // ---- Overlay / add mode ---------------------------------------------------
  function setOverlay(on) {
    overlayOn = on;
    if (!on) { setAddMode(false); closeFilterPanel(); closeCard(); closeComposer(); }
    renderPins();
    refreshFab();
    applyDim();
    applyBlocker();
  }
  // show/hide the whole toolbar (the lone Notes button when collapsed)
  function setBar(on) {
    barOn = on;
    if (fab) fab.style.display = on ? '' : 'none';
    if (!on) setOverlay(false);   // hiding the toolbar also hides the overlay
  }
  // semi-transparent backdrop that darkens the page behind the notes
  function applyDim() {
    if (dimLevel > 0 && overlayOn && root) {
      if (!dimEl) { dimEl = document.createElement('div'); dimEl.className = 'sma-dim'; root.insertBefore(dimEl, root.firstChild); }
      dimEl.style.background = 'rgba(0,0,0,' + dimLevel + ')';
    } else if (dimEl) { dimEl.remove(); dimEl = null; }
  }
  // transparent layer that absorbs clicks so a near-miss doesn't hit the page;
  // off during add mode so you can still click elements to anchor notes
  function applyBlocker() {
    if (overlayOn && !addMode && root) {
      if (!blockerEl) {
        blockerEl = document.createElement('div');
        blockerEl.className = 'sma-blocker';
        blockerEl.addEventListener('click', function (e) { e.stopPropagation(); e.preventDefault(); });
      }
      root.insertBefore(blockerEl, root.firstChild);   // below pins/cards/toolbar
    } else if (blockerEl) { blockerEl.remove(); }
  }
  function setAddMode(on) {
    addMode = on;
    document.body.style.cursor = on ? 'crosshair' : '';
    if (addBtn) { addBtn.textContent = on ? 'Cancel' : '+ Add note'; addBtn.classList.toggle('sma-on', on); }
    if (highlight) highlight.style.display = 'none';
    if (on) closeCard();
    showHint(on ? 'Click an element to attach a note' : null);
    applyBlocker();
  }
  function showHint(text) {
    if (hint) { hint.remove(); hint = null; }
    if (!text) return;
    hint = document.createElement('div'); hint.className = 'sma-hint'; hint.textContent = text;
    root.appendChild(hint);
  }
  function targetUnder(x, y) {
    if (highlight) highlight.style.display = 'none';
    var stack = document.elementsFromPoint(x, y);
    for (var i = 0; i < stack.length; i++) { if (!isOurs(stack[i])) return stack[i]; }
    return null;
  }

  function highlightEl(el) {
    if (!el) { highlight.style.display = 'none'; return; }
    var r = el.getBoundingClientRect();
    highlight.style.display = 'block';
    highlight.style.left = r.left + 'px'; highlight.style.top = r.top + 'px';
    highlight.style.width = r.width + 'px'; highlight.style.height = r.height + 'px';
  }

  document.addEventListener('mousemove', function (e) {
    if (!addMode) return;
    hoverEl = targetUnder(e.clientX, e.clientY);   // mouse re-selects the element under it
    highlightEl(hoverEl);
  }, true);

  document.addEventListener('click', function (e) {
    if (!addMode) return;
    if (isOurs(e.target)) return;
    e.preventDefault(); e.stopPropagation();
    var el = hoverEl || targetUnder(e.clientX, e.clientY);
    if (!el) return;
    var r = el.getBoundingClientRect();
    var clamp = function (v) { return Math.min(1, Math.max(0, v)); };
    var relX = r.width ? clamp((e.clientX - r.left) / r.width) : 0.5;
    var relY = r.height ? clamp((e.clientY - r.top) / r.height) : 0.5;
    openComposer(el, relX, relY);
    setAddMode(false);
  }, true);

  // ---- Pins -----------------------------------------------------------------
  function stylePin(n) {
    var p = n._pin; if (!p) return;
    if (n.completed) {                                // done: green bubble + happy face
      p.style.background = '#22c55e';
      p._num.textContent = '☺';
    } else {                                          // open: priority colour + number
      p.style.background = priorityColor(n.priority);
      p._num.textContent = n.priority;
    }
    p.classList.toggle('sma-done', !!n.completed);
    p._user.textContent = n.author_letter;            // sub-bubble = user marker
    p._user.style.background = displayColor(n.author_name);
  }
  function renderPins() {
    notes.forEach(function (n) { if (!overlayOn && n._pin) { n._pin.remove(); n._pin = null; } });
    if (!overlayOn) return;
    notes.forEach(function (n) {
      if (!n._pin) {
        var pin = document.createElement('div'); pin.className = 'sma-pin';
        var num = document.createElement('span'); num.className = 'sma-pin-num';
        var user = document.createElement('span'); user.className = 'sma-pin-user';
        pin.appendChild(num); pin.appendChild(user);
        pin._num = num; pin._user = user;
        pin.addEventListener('click', function (n) {
          return function (ev) { ev.stopPropagation(); toggleCard(n); };
        }(n));
        // hovering a pin highlights the element it's anchored to (not in add mode)
        pin.addEventListener('mouseenter', function (n) {
          return function () { if (addMode) return; var res = resolveAnchor(n); if (res) highlightEl(res.el); };
        }(n));
        pin.addEventListener('mouseleave', function () { if (!addMode) highlightEl(null); });
        n._pin = pin; root.appendChild(pin);
      }
      stylePin(n);
    });
  }
  function tick() {
    if (overlayOn) {
      var orphan = 0;
      notes.forEach(function (n) {
        if (!n._pin) return;
        var allowed = !!visiblePri[n.priority] && authorShown(n.author_name);
        if (!allowed) {
          n._pin.style.display = 'none';
          if (activeCard && activeCard.note === n) closeCard();
          return;
        }
        var res = resolveAnchor(n);
        if (res) {
          var r = res.el.getBoundingClientRect();
          var onscreen = isVisible(res.el) && r.bottom > 0 && r.top < innerHeight;
          n._pin.classList.remove('sma-orphan'); n._pin.removeAttribute('title');
          if (onscreen) {
            n._pin.style.display = 'flex';
            n._pin.style.left = (r.left + res.rx * r.width) + 'px';
            n._pin.style.top = (r.top + res.ry * r.height) + 'px';
          } else {
            n._pin.style.display = 'none';
            if (activeCard && activeCard.note === n) closeCard();
          }
        } else {
          // can't find the element it was attached to: park it in a visible
          // stack (top-right, clear of the toolbar) so the note is never lost
          n._pin.classList.add('sma-orphan');
          n._pin.title = "Couldn't find where this note was attached";
          n._pin.style.display = 'flex';
          n._pin.style.left = (document.documentElement.clientWidth - 34) + 'px';
          n._pin.style.top = (60 + orphan * 42) + 'px';
          orphan++;
        }
      });
      if (activeCard && activeCard.note._pin && activeCard.note._pin.style.display !== 'none') {
        var p = activeCard.note._pin;
        placeCard(activeCard.el, parseFloat(p.style.left), parseFloat(p.style.top));
      }
    }
    requestAnimationFrame(tick);
  }

  // ---- Card -----------------------------------------------------------------
  function placeCard(card, px, py) {
    var m = 10, w = card.offsetWidth, h = card.offsetHeight;
    var left = px + 18;
    if (left + w + m > innerWidth) left = px - 18 - w;
    if (left < m) left = m;
    if (left + w + m > innerWidth) left = innerWidth - w - m;
    var top = py - h / 2;
    if (top + h + m > innerHeight) top = innerHeight - h - m;
    if (top < m) top = m;
    card.style.left = left + 'px'; card.style.top = top + 'px';
  }
  function closeCard() { if (activeCard) { activeCard.el.remove(); activeCard = null; } dirtyCheck = null; }
  function toggleCard(note) {
    if (activeCard && activeCard.note === note) { if (okToDiscard()) closeCard(); return; }
    if (!okToDiscard()) return;          // don't abandon a dirty editor
    closeCard();
    var card = document.createElement('div'); card.className = 'sma-card';
    renderCardView(card, note);
    root.appendChild(card);
    activeCard = { note: note, el: card };
    var p = note._pin;
    if (p) placeCard(card, parseFloat(p.style.left), parseFloat(p.style.top));
  }
  // open a note's card and scroll to it — works even if the pin is hidden/orphaned
  function openCardForNote(note) {
    if (!okToDiscard()) return;
    closeCard();
    var card = document.createElement('div'); card.className = 'sma-card';
    renderCardView(card, note);
    root.appendChild(card);
    activeCard = { note: note, el: card };
    var res = computeAnchor(note);
    if (res && res.el) {
      var r = res.el.getBoundingClientRect();
      placeCard(card, r.left + res.rx * r.width, r.top + res.ry * r.height);
    } else {
      placeCard(card, innerWidth / 2, innerHeight / 2);
    }
  }
  function revealNote(note) {
    var res = computeAnchor(note);
    if (res && res.el && res.el.scrollIntoView) {
      try { res.el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { res.el.scrollIntoView(); }
    }
    openCardForNote(note);
  }
  function addClose(card, fn) {
    var x = document.createElement('button'); x.className = 'sma-close'; x.textContent = '×';
    x.onclick = fn || closeCard; card.appendChild(x);
  }

  function renderCardView(card, note) {
    dirtyCheck = null;                    // leaving any editor for read-only view
    card.innerHTML = ''; addClose(card);
    var meta = document.createElement('div'); meta.className = 'sma-meta';
    meta.appendChild(badgeEl(note.author_letter, displayColor(note.author_name), 'sma-badge'));
    var author = document.createElement('div'); author.className = 'sma-author'; author.textContent = note.author_name;
    meta.appendChild(author);

    var body = document.createElement('div'); body.className = 'sma-body'; body.textContent = note.body;

    var ctl = document.createElement('div'); ctl.className = 'sma-ctl sma-ctl-top';
    var doneLabel = document.createElement('label');
    var chk = document.createElement('input'); chk.type = 'checkbox'; chk.checked = !!note.completed;
    chk.onchange = function () {
      updateNote(note.id, { completed: chk.checked }).then(function (row) {
        note.completed = row.completed; stylePin(note);
      }).catch(function (e) { alert('Update failed: ' + e.message); chk.checked = !chk.checked; });
    };
    doneLabel.appendChild(chk); doneLabel.appendChild(document.createTextNode(' Complete'));

    var priWrap = document.createElement('label'); priWrap.appendChild(document.createTextNode('Priority '));
    var sel = document.createElement('select');
    for (var i = 1; i <= 3; i++) {
      var o = document.createElement('option'); o.value = i; o.textContent = i;
      if (note.priority === i) o.selected = true; sel.appendChild(o);
    }
    sel.onchange = function () {
      var v = parseInt(sel.value, 10);
      updateNote(note.id, { priority: v }).then(function (row) { note.priority = row.priority; stylePin(note); })
        .catch(function (e) { alert('Update failed: ' + e.message); });
    };
    priWrap.appendChild(sel);
    ctl.appendChild(doneLabel); ctl.appendChild(priWrap);

    var actions = document.createElement('div'); actions.className = 'sma-actions';
    actions.appendChild(mkLink('Edit', false, function () { renderCardEdit(card, note); }));
    actions.appendChild(mkLink('Delete', true, function () {
      if (!confirm('Delete this note?')) return;
      deleteNote(note.id).then(function () {
        if (note._pin) note._pin.remove();
        notes = notes.filter(function (n) { return n.id !== note.id; });
        closeCard();
      }).catch(function (e) { alert('Delete failed: ' + e.message); });
    }));

    card.appendChild(ctl); card.appendChild(meta); card.appendChild(body);
    card.appendChild(actions); card.appendChild(buildThread(card, note));
  }

  function buildThread(card, note) {
    var thread = document.createElement('div'); thread.className = 'sma-thread';
    (note.replies || []).forEach(function (rep) {
      var row = document.createElement('div'); row.className = 'sma-reply';
      row.appendChild(badgeEl(rep.author_letter, displayColor(rep.author_name), 'sma-badge'));
      var main = document.createElement('div'); main.className = 'sma-reply-main';
      var a = document.createElement('div'); a.className = 'sma-reply-author'; a.textContent = rep.author_name;
      var b = document.createElement('div'); b.className = 'sma-reply-body'; b.textContent = rep.body;
      main.appendChild(a); main.appendChild(b); row.appendChild(main); thread.appendChild(row);
    });
    var actions = document.createElement('div'); actions.className = 'sma-actions';
    actions.appendChild(mkLink('Reply', false, function () { openReply(card, note, thread); }));
    thread.appendChild(actions);
    return thread;
  }
  function openReply(card, note, thread) {
    var ta = document.createElement('textarea'); ta.placeholder = 'Write a reply…';
    var actions = document.createElement('div'); actions.className = 'sma-actions sma-split';
    actions.appendChild(mkLink('Save', false, function () {
      var val = ta.value.trim(); if (!val) return;
      var rep = { id: uid(), author_name: me, author_letter: letterFor(me), author_color: colorFor(me), body: val, created_at: new Date().toISOString() };
      var next = (note.replies || []).concat([rep]);
      updateNote(note.id, { replies: next }).then(function (row) {
        note.replies = Array.isArray(row.replies) ? row.replies : next;
        renderCardView(card, note);
      }).catch(function (e) { alert('Reply failed: ' + e.message); });
    }));
    actions.insertBefore(mkLink('Cancel', false, function () { renderCardView(card, note); }), actions.firstChild);
    thread.appendChild(ta); thread.appendChild(actions); ta.focus();
  }
  function renderCardEdit(card, note) {
    card.innerHTML = ''; addClose(card, function () { if (okToDiscard()) closeCard(); });
    var ta = document.createElement('textarea'); ta.value = note.body;
    dirtyCheck = function () { return ta.value !== note.body; };
    var actions = document.createElement('div'); actions.className = 'sma-actions sma-split';
    actions.appendChild(mkLink('Save', false, function () {
      updateNote(note.id, { body: ta.value.trim(), updated_at: new Date().toISOString() })
        .then(function (row) { note.body = row.body; renderCardView(card, note); })
        .catch(function (e) { alert('Save failed: ' + e.message); });
    }));
    actions.insertBefore(mkLink('Cancel', false, function () { if (okToDiscard()) renderCardView(card, note); }), actions.firstChild);
    card.appendChild(ta); card.appendChild(actions); ta.focus();
  }
  function closeComposer() { if (composer) { composer.remove(); composer = null; } dirtyCheck = null; }
  // confirm before discarding an editor that has unsaved changes
  function okToDiscard() { return !(dirtyCheck && dirtyCheck()) || confirm('Discard your unsaved changes?'); }
  // close the composer but stay in note mode (a 2nd Escape exits note mode)
  function exitComposer() { if (okToDiscard()) { closeComposer(); setAddMode(true); } }
  function openComposer(el, relX, relY) {
    closeCard(); closeComposer();
    var card = document.createElement('div'); card.className = 'sma-card'; addClose(card, exitComposer);
    composer = card;
    var state = { completed: false, priority: 3 };
    var ctl = buildLocalControls(state);
    var ta = document.createElement('textarea'); ta.placeholder = 'Write a note…';
    dirtyCheck = function () { return ta.value.trim() !== '' || state.completed || state.priority !== 3; };
    var actions = document.createElement('div'); actions.className = 'sma-actions sma-split';
    actions.appendChild(mkLink('Save', false, function () {
      var val = ta.value.trim(); if (!val) { closeComposer(); return; }
      var anchor = buildAnchor(el, relX, relY);
      var row = {
        page_path: PAGE_PATH, author_name: me, author_letter: letterFor(me), author_color: colorFor(me),
        body: val, priority: state.priority, completed: state.completed,
        anchor: anchor,
        anchor_selector: anchor.sel, anchor_tag: anchor.tag, anchor_text: anchor.text,
        rel_x: relX, rel_y: relY
      };
      createNote(row).then(function (saved) {
        saved._res = { el: el, rx: relX, ry: relY }; saved._pin = null;
        if (!Array.isArray(saved.replies)) saved.replies = [];
        notes.push(saved); recomputeAuthorColors(); closeComposer(); renderPins();
        setAddMode(true);                 // stay in note mode to add more
      }).catch(function (e) { alert('Save failed: ' + e.message); });
    }));
    actions.insertBefore(mkLink('Cancel', false, exitComposer), actions.firstChild);
    card.appendChild(ctl); card.appendChild(ta); card.appendChild(actions); root.appendChild(card);
    var r = el.getBoundingClientRect();
    placeCard(card, r.left + relX * r.width, r.top + relY * r.height); ta.focus();
  }

  // ---- Filter panel ---------------------------------------------------------
  function authorShown(name) { return visibleAuthors[name] !== false; }
  function distinctAuthors() {
    var seen = {}, list = [];
    notes.forEach(function (n) {
      if (!seen[n.author_name]) { seen[n.author_name] = 1; list.push({ name: n.author_name, color: n.author_color }); }
    });
    return list;
  }
  function priSoloed(p) { for (var k = 1; k <= 3; k++) { if ((k === p) !== !!visiblePri[k]) return false; } return true; }
  function priCount(p) { return notes.filter(function (n) { return n.priority === p; }).length; }
  function authorSoloed(name, names) {
    for (var i = 0; i < names.length; i++) { if ((names[i] === name) !== authorShown(names[i])) return false; }
    return true;
  }

  function filterRow(opts) {
    // opts: {checked, dot, label, soloed, onToggle, onSolo}
    var row = document.createElement('div'); row.className = 'sma-frow';
    var lab = document.createElement('label');
    var cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = opts.checked;
    cb.onchange = function () { opts.onToggle(cb.checked); };
    var dot = document.createElement('span'); dot.className = 'sma-dot'; dot.style.background = opts.dot;
    lab.appendChild(cb); lab.appendChild(dot); lab.appendChild(document.createTextNode(' ' + opts.label));
    var solo = document.createElement('button');
    solo.className = 'sma-solo' + (opts.soloed ? ' sma-on' : ''); solo.textContent = 'Solo';
    if (opts.soloDisabled) { solo.disabled = true; solo.title = 'No notes at this priority'; }
    else { solo.onclick = opts.onSolo; }
    row.appendChild(lab); row.appendChild(solo);
    return row;
  }

  function toggleFilterPanel() {
    if (filterPanel) { closeFilterPanel(); return; }
    filterPanel = document.createElement('div'); filterPanel.className = 'sma-filter';

    // dim-background slider (0–45%)
    var dimRow = document.createElement('div'); dimRow.className = 'sma-frow';
    var dlab = document.createElement('span'); dlab.textContent = 'Dim'; dlab.style.cssText = 'flex:0 0 auto;color:#666';
    var slider = document.createElement('input');
    slider.type = 'range'; slider.min = '0'; slider.max = '45'; slider.step = '1';
    slider.value = String(Math.round(dimLevel * 100));
    var dval = document.createElement('span'); dval.className = 'sma-dimval'; dval.textContent = slider.value + '%';
    slider.oninput = function () {
      dimLevel = parseInt(slider.value, 10) / 100;
      localStorage.setItem('sma_dim_level', String(dimLevel));
      dval.textContent = slider.value + '%';
      applyDim();
    };
    dimRow.appendChild(dlab); dimRow.appendChild(slider); dimRow.appendChild(dval);
    filterPanel.appendChild(dimRow);

    var ph = document.createElement('div'); ph.className = 'sma-fhead'; ph.textContent = 'Priority'; filterPanel.appendChild(ph);
    for (var i = 1; i <= 3; i++) {
      (function (p) {
        filterPanel.appendChild(filterRow({
          checked: !!visiblePri[p], dot: priorityColor(p), label: 'Priority ' + p, soloed: priSoloed(p),
          soloDisabled: priCount(p) === 0,
          onToggle: function (v) { visiblePri[p] = v; },
          onSolo: function () {
            if (priSoloed(p)) { for (var k = 1; k <= 3; k++) visiblePri[k] = true; }
            else { for (var k = 1; k <= 3; k++) visiblePri[k] = (k === p); }
            rebuildFilterPanel();
          }
        }));
      })(i);
    }

    var authors = distinctAuthors();
    if (authors.length) {
      var ah = document.createElement('div'); ah.className = 'sma-fhead'; ah.textContent = 'Author';
      filterPanel.appendChild(ah);
      var names = authors.map(function (a) { return a.name; });
      authors.forEach(function (a) {
        var cnt = notes.filter(function (n) { return n.author_name === a.name; }).length;
        filterPanel.appendChild(filterRow({
          checked: authorShown(a.name), dot: displayColor(a.name), label: a.name + ' (' + cnt + ')', soloed: authorSoloed(a.name, names),
          onToggle: function (v) { visibleAuthors[a.name] = v; },
          onSolo: function () {
            if (authorSoloed(a.name, names)) { names.forEach(function (n) { visibleAuthors[n] = true; }); }
            else { names.forEach(function (n) { visibleAuthors[n] = (n === a.name); }); }
            rebuildFilterPanel();
          }
        }));
      });
    }

    var all = document.createElement('button'); all.className = 'sma-fall'; all.textContent = 'Show all';
    all.onclick = function () {
      for (var k = 1; k <= 3; k++) visiblePri[k] = true;
      visibleAuthors = {};
      rebuildFilterPanel();
    };
    filterPanel.appendChild(all);

    // clickable list of the currently-shown notes (find/jump to any, even if
    // its pin can't render because the element is hidden or has moved)
    var shown = notes.filter(function (n) { return visiblePri[n.priority] && authorShown(n.author_name); });
    if (shown.length) {
      var lh = document.createElement('div'); lh.className = 'sma-fhead'; lh.textContent = 'Notes (' + shown.length + ')';
      filterPanel.appendChild(lh);
      shown.forEach(function (n) {
        var row = document.createElement('div'); row.className = 'sma-noterow';
        if (!computeAnchor(n)) row.classList.add('sma-off');   // dim ones that can't place
        row.appendChild(badgeEl(n.author_letter, displayColor(n.author_name), 'sma-badge'));
        var txt = document.createElement('span'); txt.className = 'sma-notetext';
        txt.textContent = (n.body || '(no text)').replace(/\s+/g, ' ').trim().slice(0, 42) || '(no text)';
        row.appendChild(txt);
        row.onclick = function () { closeFilterPanel(); revealNote(n); };
        filterPanel.appendChild(row);
      });
    }

    root.appendChild(filterPanel); filterBtn.classList.add('sma-on');
    var fr = filterBtn.getBoundingClientRect();   // open beside the View button
    filterPanel.style.left = (fr.right + 8) + 'px';
    filterPanel.style.top = fr.top + 'px';
  }
  function rebuildFilterPanel() { closeFilterPanel(); toggleFilterPanel(); }
  function closeFilterPanel() {
    if (filterPanel) { filterPanel.remove(); filterPanel = null; }
    if (filterBtn) filterBtn.classList.remove('sma-on');
  }

  // ---- helpers --------------------------------------------------------------
  function badgeEl(letter, color, cls) {
    var b = document.createElement('div'); b.className = cls; b.textContent = letter; b.style.background = color; return b;
  }
  function mkLink(text, danger, fn) {
    var b = document.createElement('button'); b.className = 'sma-link' + (danger ? ' sma-danger' : '');
    b.textContent = text; b.onclick = fn; return b;
  }
  // Complete + Priority controls bound to a local state object (for the composer)
  function buildLocalControls(state) {
    var ctl = document.createElement('div'); ctl.className = 'sma-ctl sma-ctl-top';
    var doneLabel = document.createElement('label');
    var chk = document.createElement('input'); chk.type = 'checkbox'; chk.checked = !!state.completed;
    chk.onchange = function () { state.completed = chk.checked; };
    doneLabel.appendChild(chk); doneLabel.appendChild(document.createTextNode(' Complete'));
    var priWrap = document.createElement('label'); priWrap.appendChild(document.createTextNode('Priority '));
    var sel = document.createElement('select');
    for (var i = 1; i <= 3; i++) {
      var o = document.createElement('option'); o.value = i; o.textContent = i;
      if (state.priority === i) o.selected = true; sel.appendChild(o);
    }
    sel.onchange = function () { state.priority = parseInt(sel.value, 10); };
    priWrap.appendChild(sel);
    ctl.appendChild(doneLabel); ctl.appendChild(priWrap);
    return ctl;
  }

  // ---- Account menu (popout with Sign out) ----------------------------------
  function onDocDown(e) {
    if (!userMenu) return;
    var who = fab && fab._who;
    if (userMenu.contains(e.target) || (who && who.contains(e.target))) return;
    closeUserMenu();
  }
  function closeUserMenu() {
    if (userMenu) { userMenu.remove(); userMenu = null; }
    document.removeEventListener('mousedown', onDocDown, true);
  }
  function toggleUserMenu() {
    if (userMenu) { closeUserMenu(); return; }
    ensureRoot();
    userMenu = document.createElement('div'); userMenu.className = 'sma-menu';
    var id = document.createElement('div'); id.className = 'sma-menu-id';
    id.textContent = (me || '') + (session && session.email ? ' · ' + session.email : '');
    var out = document.createElement('button'); out.className = 'sma-menu-out';
    out.textContent = 'Sign out'; out.onclick = signOut;
    userMenu.appendChild(id); userMenu.appendChild(out);
    root.appendChild(userMenu);
    var r = fab._who.getBoundingClientRect();
    userMenu.style.left = (r.right + 8) + 'px';   // beside the identity button
    userMenu.style.top = r.top + 'px';
    setTimeout(function () { document.addEventListener('mousedown', onDocDown, true); }, 0);
  }

  // ---- Sign out -------------------------------------------------------------
  function signOut() {
    closeUserMenu();
    closeCard(); closeFilterPanel(); setOverlay(false);
    var tok = session && session.access_token;
    if (tok) {
      fetch(SUPABASE_URL + '/auth/v1/logout', {
        method: 'POST', headers: { apikey: SUPABASE_KEY, Authorization: 'Bearer ' + tok }
      }).catch(function () { });
    }
    clearSession();
    if (root) { root.remove(); root = null; fab = null; filterPanel = null; dimEl = null; blockerEl = null; }
  }

  // ---- Shortcut + boot ------------------------------------------------------
  document.addEventListener('keydown', function (e) {
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'A' || e.key === 'a')) {
      e.preventDefault();
      if (!session) showLogin();
      else if (!fab) { start(); }
      else setBar(!barOn);
    } else if (e.key === 'Escape' && (composer || (activeCard && dirtyCheck) || addMode)) {
      e.preventDefault();
      if (composer) { exitComposer(); }                       // 1st: close the note, stay in note mode
      else if (activeCard && dirtyCheck) { if (okToDiscard()) closeCard(); }  // editing existing note
      else { setAddMode(false); }                             // 2nd: exit note mode
    } else if (e.key === 'Enter' && overlayOn && !addMode && !composer) {
      // Enter opens add-note mode, unless typing in a field or on a control
      var t = e.target, tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON' || (t && t.isContentEditable)) return;
      e.preventDefault();
      setAddMode(true);
    } else if (addMode && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      // refine the targeted element: up = parent, down = first child
      e.preventDefault();
      if (!hoverEl) return;
      if (e.key === 'ArrowUp') {
        var par = hoverEl.parentElement;
        if (par && par !== document.body && par !== document.documentElement && !isOurs(par)) { hoverEl = par; highlightEl(hoverEl); }
      } else {
        var ch = hoverEl.firstElementChild;
        if (ch && !isOurs(ch)) { hoverEl = ch; highlightEl(hoverEl); }
      }
    }
  });

  function start() {
    ensureRoot();
    buildUI();
    barOn = true;
    if (!booted) { booted = true; requestAnimationFrame(tick); }
    refreshFab();
    loadNotes();
  }

  // returning admin: validate/refresh the stored session, then show the tool
  session = loadSession();
  if (session) {
    me = session.name;
    refresh().then(function (ok) { if (ok) start(); else clearSession(); });
  }
  // first-time / signed-out: press Ctrl/Cmd+Shift+A to open the login form
})();
